import { Card, Icon } from '@salesforce/design-system-react';
import { useShallow } from 'zustand/react/shallow';

import { IconCategory } from '../../../shared/types/Enum';
import { useQualityFormulaStore } from '../store/QualityFormulaStoreProvider';
import { ImpactCard } from './ImpactCard';
import { SummaryCard } from './SummaryCard';

const resultsHeading = (
  <span className="qfw-template-results-heading">
    <Icon category={IconCategory.Utility} name="chart" size="small" style={{ fill: 'var(--slds-g-color-accent-1)' }} />
    <span>Review Results</span>
  </span>
);

/** The workbench hides results until tested; the template's adjacent results column shows a prompt. */
export const QualityResultsSection: React.FC<{ compact?: boolean; inline?: boolean; showEmptyState?: boolean }> = ({
  compact = false,
  inline = false,
  showEmptyState = false,
}) => {
  const { results, summary } = useQualityFormulaStore(
    useShallow(store => ({ results: store.results, summary: store.summary })),
  );
  if (results.length === 0 || summary === null) {
    return showEmptyState ? (
      <section aria-label="Formula test results" className="qfw-template-results">
        <Card className="qfw-result-card qfw-template-results-empty" heading={resultsHeading}>
          <div className="qfw-template-results-prompt">
            <Icon
              category={IconCategory.Utility}
              name="chart"
              size="medium"
              style={{ fill: 'var(--slds-g-color-accent-1)' }}
            />
            <h3 className="slds-text-heading_small">Ready to review</h3>
            <p className="slds-text-body_regular slds-text-color_weak">
              Test this formula to see its impact on price and delivered quantity.
            </p>
          </div>
        </Card>
      </section>
    ) : null;
  }

  if (inline) {
    return (
      <section aria-label="Formula test results" className="qfw-inline-results">
        <h2 className="slds-text-heading_small">Result</h2>
        <SummaryCard inline summary={summary} />
        <div className="qfw-impact-list">
          <h3 className="slds-text-heading_small">Impact by spec</h3>
          {results.map(result => (
            <ImpactCard inline key={result.id} result={result} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="Formula test results"
      className={compact ? 'qfw-template-results' : ''}
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      <div>
        <h2 className="slds-text-heading_small">{compact ? resultsHeading : 'Review Results'}</h2>
        <p className="slds-text-body_regular slds-text-color_weak" style={{ maxWidth: 640 }}>
          See how each quality spec changes price or delivered quantity.
        </p>
      </div>

      <div className="slds-grid slds-wrap slds-gutters_small">
        <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2 slds-large-size_1-of-3 slds-p-bottom_small">
          <SummaryCard summary={summary} />
        </div>
        {results.map(result => (
          <div
            className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2 slds-large-size_1-of-3 slds-p-bottom_small"
            key={result.id}
          >
            <ImpactCard result={result} />
          </div>
        ))}
      </div>
    </section>
  );
};
